import * as React from 'react'
import { useState, useEffect } from 'react'
import {
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
} from '@patternfly/react-table';
import {
    Link,
    useParams
} from "react-router-dom";

import {
    Button,
    Modal,
    ModalVariant,
    Popover,
    ListItem,
    List,
    TooltipProps,
    Tooltip,
    Spinner,
} from '@patternfly/react-core';
import {
    ResourceItem,
    ResourceItemVariants,
    ResourceItemType
} from '@app/components/ResourceItem'
import {
    ActionMenu
} from '@app/components/ActionMenu'
import { SecretRef, ChangeRequestEvent, ChangeRequestAction } from '../../../../../shared/secretmanager/changerequests/types'
import { EyeIcon, EyeSlashIcon, ExclamationCircleIcon, ExclamationTriangleIcon, CheckIcon } from '@patternfly/react-icons'
import Timestamp from 'react-timestamp';

import { ChangeRequestDetail } from '../../../../../shared/secretmanager/changerequests/types';
import { SECRETS_ERROR_IDENTIFIER, WARNING_FINDER } from '../../../../../shared/secretmanager/changerequests/errors';
import { jiraUrlFor } from '../../../../../shared/secretmanager/changerequests/jira';
import { useChangeRequestDetail, useUnsealChangeRequestSecret, useTriggerChangeRequestAction } from '../../hooks/useSecretManagerData';
import { useClientEvent } from '@app/hooks/useClientEvent';

interface HiddenTextProps {
    onReveal: () => Promise<string>
    reveal?: { enabled: boolean, reason?: { code: string, message: string }[] }
}

const HiddenText: React.FunctionComponent<HiddenTextProps> = ({ onReveal, reveal }) => {

    const [hidden, setHidden] = useState<boolean>(true)
    const [value, setValue] = useState<string>()
    const [loading, setLoading] = useState<boolean>(false)

    const disabled = reveal?.enabled === false || reveal === undefined;
    const disabled_reason = reveal?.enabled === false ? <List>{reveal?.reason?.map(reason => <ListItem>{reason.message}</ListItem>)}</List> : "Unseal is not allowed on the current state."

    async function onEyeClick() {
        if (!value) {
            setLoading(true)
            const value = await onReveal();
            setValue(value);
            setLoading(false);
        }
        setHidden(!hidden);
    }

    const EyeButton = () => <Button onClick={onEyeClick} isAriaDisabled={disabled} isLoading={loading} variant="plain" aria-label="Action" isInline component="a" style={{ padding: 0, paddingLeft: 6 }}>
        {hidden ? <EyeIcon /> : <EyeSlashIcon />}
    </Button>;

    return (
        <div className="dx-split" style={{ flexWrap: 'wrap' }}>
            <div>
                {hidden ? '••••••••••' : value}
            </div>
            <div>
                { disabled ? <Tooltip content={disabled_reason}><EyeButton /></Tooltip> : <EyeButton />}
            </div>
        </div>
    )
}


interface SecretsTableProps {
    proposal: number,
    reveal?: {
        enabled: boolean,
        reason?: { code: string, message: string }[]
    },
    secrets: SecretRef[],
    warnings: any[]
}


const SecretsTable: React.FunctionComponent<SecretsTableProps> = ({ proposal, secrets, reveal, warnings }) => {

    const headers = ['Keyvault', 'Key', 'Value', 'Status']

    const unsealMutation = useUnsealChangeRequestSecret();

    async function onReveal(keyvault, key): Promise<string> {
        const result = await unsealMutation.mutateAsync({ id: proposal, secret: { vault: keyvault, name: key } });
        return result.value;
    }

    return (<>
        <Table variant='compact'>
            <Thead>
                <Tr>
                    {headers.map(header => {
                        return <Th width={30}>{header}</Th>
                    })}
                </Tr>
            </Thead>
            <Tbody>
                {secrets.map((secret, index) => {
                    const errors = WARNING_FINDER(warnings, SECRETS_ERROR_IDENTIFIER, secret);

                    let alert: React.ReactElement | undefined = undefined;
                    if (errors?.length > 0) {
                        alert = <>
                            <List>
                                {errors.map(error => <ListItem>{error.message}</ListItem>)}
                            </List>
                            <br/>
                            Follow the <a href="https://teladochealth.atlassian.net/wiki/x/fffqCw">secret manager runbook</a> to get it resolved.
                            </>

                    }

                    return <Tr>
                        <Td>{secret.vault}</Td>
                        <Td>{secret.name}</Td>
                        <Td><HiddenText reveal={reveal} onReveal={async () => { return onReveal(secret.vault, secret.name) }} /></Td>
                        <Td>
                            {alert ?
                                <Popover
                                    triggerAction="hover"
                                    headerContent='System errors'
                                    alertSeverityVariant='danger'
                                    headerIcon={<ExclamationTriangleIcon />}
                                    bodyContent={alert}
                                >
                                    <span className="dx-icon dx-icon--inline dx-icon--md dx-icon--danger"><ExclamationTriangleIcon /></span>
                                </Popover>
                                :
                                <span className="dx-icon dx-icon--inline dx-icon--md dx-icon--success"><CheckIcon /></span>
                            }
                        </Td>
                    </Tr>
                })}
            </Tbody>
        </Table>
    </>)
}

interface HistoryTableProps {
    history: ChangeRequestEvent[]
}

export const HistoryTable: React.FunctionComponent<HistoryTableProps> = ({ history }) => {

    return (
        <Table variant='compact'>
            <Thead>
                <Tr>
                    <Th>Date</Th>
                    <Th>Author</Th>
                    <Th>State</Th>
                </Tr>
            </Thead>
            <Tbody>
                {history.map(h => {
                    return (
                        <Tr>
                            <Td>
                                <Timestamp date={h.date} options={{ includeDay: false }} />
                            </Td>
                            <Td>{h.author.name || h.author.oid}</Td>
                            <Td>{h.type}</Td>
                        </Tr>
                    )
                })}
            </Tbody>
        </Table>
    )
}

interface ChangeRequestPageProps {
    root: string
}

enum AlertSeverity {
    OK = 'success',
    WARNING = 'warning',
    ERROR = 'danger'
}

interface Alert {
    severity: AlertSeverity
    title: string,
    message: string,
}

export const AlertModal: React.FunctionComponent<{ alert: Alert | undefined }> = ({ alert }) => {

    const [visible, setVisible] = useState<boolean>(false);

    useEffect(() => {
        setVisible(true);
    }, [alert])

    if (!alert) {
        return <></>
    }

    return (
        <Modal
            variant={ModalVariant.small}
            title={alert.title}
            isOpen={visible}
            titleIconVariant={alert.severity}
            onClose={() => { setVisible(false) }}
            showClose={false}
            actions={[
                <Button key="confirm" variant="primary" onClick={() => { setVisible(false) }}>
                    Accept
                </Button>
            ]}
        >
            {alert.message}
        </Modal>
    )
}

export const ChangeRequestDisplay: React.FunctionComponent<{ details: ChangeRequestDetail, root: string }> = ({ details, root }) => {
    const [alert, setAlert] = useState<Alert>();

    const { request_id } = useParams<{ request_id: string }>();

    const Actions: React.FunctionComponent<{ details: ChangeRequestDetail }> = ({ details }) => {

    const triggerMutation = useTriggerChangeRequestAction();
        const fireEvent = useClientEvent();

        const on_action_select = async (id: number, action: ChangeRequestAction) => {
            try {
                await triggerMutation.mutateAsync({ id, action });
                // CHANGE_REQUEST_APPROVED/UNAPPROVED/APPLIED/ROLLED_BACK/CANCELLED/DISMISSED are
                // already fired by the backend audit middleware when the corresponding
                // POST /api/secretmanager/changerequests/{id}/{action} endpoint is hit.
                // Only UNSEAL needs a FE event because POST .../changerequests/{id}/unseal
                // is a different URL from GET .../secretsexplorer/unseal (the backend mapping).
                if (action === ChangeRequestAction.UNSEAL) {
                  fireEvent('SECRET_UNSEALED', { id });
                }
                window.location.reload()
            } catch (error) {
                if (error instanceof Error) {
                    const alert: Alert = {
                        title: 'There was an error',
                        message: error.message,
                        severity: AlertSeverity.ERROR
                    }

                    setAlert(alert);
                }
            }
        }

        const actions = details.transitions.map(operation => {
            const action = details.actions.find(action => action.type === operation)!
            let tooltip: TooltipProps | undefined = undefined;

            if (!action.enabled) {
                tooltip = {
                    content: <List>{action.reason?.map(reason => <ListItem>{reason.message}</ListItem>)}</List>,
                    flipBehavior: ['left'],
                    enableFlip: true,
                    isContentLeftAligned: true
                }
            }

            return {
                key: operation,
                children: operation,
                isAriaDisabled: !action.enabled,
                isContentLeftAligned: true,
                onClick: () => on_action_select(details.id, operation),
                tooltipProps: tooltip
            }
        })

        return (
            <span style={{ display: 'flex', alignItems: 'center', 'gap': '10px' }}>
                <span style={{ 'width': '24px', 'height': '24px' }}>
                    {triggerMutation.isPending && <Spinner size="lg" aria-label="Spinner in a heading" />}
                </span>
                <ActionMenu
                    isDisabled={details.transitions.length === 0 || triggerMutation.isPending}
                    actions={actions}
                />
            </span>
        )
    }

    const AlertBanner: React.FunctionComponent<{ warnings: any[] }> = ({ warnings }) => {

        if (warnings.length === 0) return null;

        return (
            <div className="dx-banner dx-banner--danger">
                There are errors, resolve them before proceeding. <a target='_blank' rel='noopener noreferrer' href='https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/199948157/EOD+Dashboard+Secret+Manager+-+Errors#id-[EODDashboard]SecretManager-Errors-changerequesterrors'>Learn more.</a>
            </div>
        )

    }

    function renderContent(details: ChangeRequestDetail) {
        return (
            <>
                <AlertBanner warnings={details.warnings} />
                <section className="dx-page-section">
                    <div className="dx-split dx-split--gutter" style={{ flexWrap: 'wrap' }}>
                        <div className="dx-split-item--fill">
                            <dl className="dx-description-list">
                                <div className="dx-dl-group">
                                    <dt className="dx-dl-term">ID</dt>
                                    <dd className="dx-dl-desc">{details.id}</dd>
                                </div>
                                <div className="dx-dl-group">
                                    <dt className="dx-dl-term">Author</dt>
                                    <dd className="dx-dl-desc">{details.history.at(0)!.author.name}</dd>
                                </div>
                                <div className="dx-dl-group">
                                    <dt className="dx-dl-term">Ticket</dt>
                                    <dd className="dx-dl-desc"><a target='_blank' rel='noopener noreferrer' href={jiraUrlFor(details.ticket)}>{details.ticket}</a></dd>
                                </div>
                                <div className="dx-dl-group">
                                    <dt className="dx-dl-term">Description</dt>
                                    <dd className="dx-dl-desc">{details.description}</dd>
                                </div>
                                <div className="dx-dl-group">
                                    <dt className="dx-dl-term">State</dt>
                                    <dd className="dx-dl-desc">{details.history.at(-1)!.type}</dd>
                                </div>
                            </dl>
                        </div>
                        <div>
                            <Actions details={details} />
                        </div>
                    </div>
                </section>
                <hr className="dx-divider" />
                <section className="dx-page-section">
                    <h3 className="dx-title" style={{ marginBottom: '10px' }}>Secrets</h3>
                    <SecretsTable secrets={details.secrets} proposal={details.id} reveal={details.actions.find(a => a.type === 'unseal')} warnings={details.warnings} />
                </section>
                <hr className="dx-divider" />
                <section className="dx-page-section">
                    <h3 className="dx-title" style={{ marginBottom: '10px' }}>History</h3>
                    <HistoryTable history={details.history} />
                </section>
            </>
        )
    }

    return (<>
        <AlertModal alert={alert} />
        <section className="dx-page-section">
            <nav className="dx-breadcrumb">
                <span className="dx-breadcrumb__item"><Link className="dx-breadcrumb__link" to={`${root}`}>Change requests</Link></span>
                <span className="dx-breadcrumb__item">Change request details</span>
            </nav>
            <ResourceItem variant={ResourceItemVariants.h1} type={ResourceItemType.ChangeRequest}>Change request #{request_id}</ResourceItem>


        </section>
        <hr className="dx-divider" />
        {renderContent(details!)}
    </>)
}

export const ChangeRequestPage: React.FunctionComponent<ChangeRequestPageProps> = ({ root }) => {

    const { request_id } = useParams<{ request_id: string }>();

    const { data: details, isLoading } = useChangeRequestDetail(request_id);

    if (isLoading || !details) return (
        <section className="dx-page-section">
            <div className="dx-empty-state">
                <div className="dx-empty-state__icon"><Spinner size="lg" /></div>
                <h4 className="dx-empty-state__title">Loading</h4>
            </div>
        </section>
    )

    return <ChangeRequestDisplay details={details} root={root} />
}
